import { useInfiniteQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { SortingState } from '@cast/design-system';
import {
  PersistentVolume,
  PersistentVolumeManagementStatusFilter,
  PersistentVolumePhaseFilter,
  PersistentVolumeReclaimPolicyFilter,
  PersistentVolumeStatusFilter,
} from '@cast/types-stop';
import { makeIsEnabled } from '@cast/utils';

import { useCurrentOrganizationId } from '@cast/infrastructure-organization';
import { apiClient } from '@cast/infrastructure-api';

const PERSISTENT_VOLUMES_QUERY_KEY = 'persistent-volumes';

export type PersistentVolumesFilters = {
  namespaces?: string[];
  volumeTypes?: string[];
  storageClasses?: string[];
  pvNames?: string[];
  pvcNames?: string[];
  workloadNames?: string[];
  pvPolicies?: string[];
  statuses?: PersistentVolumeStatusFilter[];
  phases?: PersistentVolumePhaseFilter[];
  reclaimPolicies?: PersistentVolumeReclaimPolicyFilter[];
  managementStatuses?: PersistentVolumeManagementStatusFilter[];
  detached?: 'yes' | 'no';
};

type UsePersistentVolumesQueryArgs = {
  clusterId?: string;
  enabled?: boolean;
  filters?: PersistentVolumesFilters;
  sortingState?: SortingState<PersistentVolume>;
  pageLimit?: number;
};

const toDetachedBoolean = (
  value: PersistentVolumesFilters['detached']
): boolean | undefined => {
  if (value === 'yes') {
    return true;
  }
  if (value === 'no') {
    return false;
  }
  return undefined;
};

export const usePersistentVolumesQuery = ({
  clusterId,
  enabled = true,
  filters,
  sortingState,
  pageLimit,
}: UsePersistentVolumesQueryArgs = {}) => {
  const organizationId = useCurrentOrganizationId();

  const sortField = sortingState?.sortBy?.accessor?.toString();
  const sortOrder = sortingState?.sortBy?.direction;

  const query = useInfiniteQuery({
    queryKey: [
      PERSISTENT_VOLUMES_QUERY_KEY,
      organizationId,
      clusterId,
      filters,
      sortField,
      sortOrder,
    ],
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.stop.listPersistentVolumes({
        organizationId,
        clusterId: clusterId!,
        namespaces: filters?.namespaces,
        volumeTypes: filters?.volumeTypes,
        storageClasses: filters?.storageClasses,
        pvNames: filters?.pvNames,
        pvcNames: filters?.pvcNames,
        workloadNames: filters?.workloadNames,
        matchedPolicyNames: filters?.pvPolicies,
        statuses: filters?.statuses,
        phases: filters?.phases,
        reclaimPolicies: filters?.reclaimPolicies,
        managementStatuses: filters?.managementStatuses,
        detached: toDetachedBoolean(filters?.detached),
        sortField,
        sortOrder,
        pageCursor: pageParam || undefined,
        pageLimit: pageLimit ? `${pageLimit}` : undefined,
      });
      return data;
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) =>
      lastPage.nextPageCursor ? lastPage.nextPageCursor : undefined,
    enabled: makeIsEnabled(!!organizationId, !!clusterId, enabled),
  });

  return {
    ...query,
    persistentVolumes: flatMap(query.data?.pages, (page) => page?.items ?? []),
    totalCount: query.data?.pages[0]?.totalCount,
  };
};
